import { useState } from 'react';
import Sidebar from '../components/Sidebar';

interface DressingRoom {
  id: string;
  name: string;
  type: 'vip' | 'standard' | 'fitting';
  capacity: number;
  status: 'available' | 'occupied' | 'maintenance';
}

interface Reservation {
  id: string;
  roomId: string;
  roomName: string;
  customerName: string;
  staff: string;
  date: string;
  startTime: string;
  endTime: string;
  purpose: string;
  dressCount: number;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
}

export default function DressingRooms() {
  const [activeTab, setActiveTab] = useState<'rooms' | 'reservations'>('rooms');
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const [rooms, setRooms] = useState<DressingRoom[]>([
    { id: '1', name: 'VIP 드레스룸 A', type: 'vip', capacity: 1, status: 'occupied' },
    { id: '2', name: 'VIP 드레스룸 B', type: 'vip', capacity: 1, status: 'available' },
    { id: '3', name: '일반 드레스룸 1', type: 'standard', capacity: 1, status: 'available' },
    { id: '4', name: '일반 드레스룸 2', type: 'standard', capacity: 1, status: 'occupied' },
    { id: '5', name: '일반 드레스룸 3', type: 'standard', capacity: 1, status: 'available' },
    { id: '6', name: '피팅룸 1', type: 'fitting', capacity: 1, status: 'available' },
    { id: '7', name: '피팅룸 2', type: 'fitting', capacity: 1, status: 'maintenance' },
  ]);

  const [reservations, setReservations] = useState<Reservation[]>([
    {
      id: '1',
      roomId: '1',
      roomName: 'VIP 드레스룸 A',
      customerName: '김민지',
      staff: '박스타일리스트',
      date: '2025-01-15',
      startTime: '14:00',
      endTime: '16:00',
      purpose: '웨딩드레스 피팅',
      dressCount: 3,
      status: 'in-progress'
    },
    {
      id: '2',
      roomId: '4',
      roomName: '일반 드레스룸 2',
      customerName: '이서연',
      staff: '최스타일리스트',
      date: '2025-01-15',
      startTime: '15:00',
      endTime: '17:00',
      purpose: '드레스 최종 선택',
      dressCount: 5,
      status: 'in-progress'
    },
    {
      id: '3',
      roomId: '2',
      roomName: 'VIP 드레스룸 B',
      customerName: '정유진',
      staff: '김스타일리스트',
      date: '2025-01-15',
      startTime: '17:00',
      endTime: '19:00',
      purpose: '본식 드레스 피팅',
      dressCount: 4,
      status: 'scheduled'
    },
  ]);

  const [newRoom, setNewRoom] = useState({ name: '', type: 'standard' as 'vip' | 'standard' | 'fitting', capacity: 1 });
  const [newReservation, setNewReservation] = useState({
    roomId: '',
    customerName: '',
    staff: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '10:00',
    endTime: '12:00',
    purpose: '',
    dressCount: 1
  });

  const timeSlots = Array.from({ length: 13 }, (_, i) => {
    const hour = i + 9;
    return `${hour.toString().padStart(2, '0')}:00`;
  });

  const handleAddRoom = () => {
    if (newRoom.name) {
      const room: DressingRoom = {
        id: Date.now().toString(),
        name: newRoom.name,
        type: newRoom.type,
        capacity: newRoom.capacity,
        status: 'available'
      };
      setRooms([...rooms, room]);
      setNewRoom({ name: '', type: 'standard', capacity: 1 });
      setShowAddRoomModal(false);
    }
  };

  const handleDeleteRoom = (id: string) => {
    if (confirm('이 드레스룸을 삭제하시겠습니까?')) {
      setRooms(rooms.filter(room => room.id !== id));
      setReservations(reservations.filter(res => res.roomId !== id));
    }
  };

  const handleAddReservation = () => {
    if (newReservation.roomId && newReservation.customerName && newReservation.staff) {
      const room = rooms.find(r => r.id === newReservation.roomId);
      const reservation: Reservation = {
        id: Date.now().toString(),
        roomId: newReservation.roomId,
        roomName: room?.name || '',
        customerName: newReservation.customerName,
        staff: newReservation.staff,
        date: newReservation.date,
        startTime: newReservation.startTime,
        endTime: newReservation.endTime,
        purpose: newReservation.purpose,
        dressCount: newReservation.dressCount,
        status: 'scheduled'
      };
      setReservations([...reservations, reservation]);
      setNewReservation({
        roomId: '',
        customerName: '',
        staff: '',
        date: new Date().toISOString().split('T')[0],
        startTime: '10:00',
        endTime: '12:00',
        purpose: '',
        dressCount: 1
      });
      setShowReservationModal(false);
    }
  };

  const getReservationForSlot = (roomId: string, time: string) => {
    return reservations.find(res => {
      if (res.roomId !== roomId || res.date !== selectedDate) return false;
      const resStart = parseInt(res.startTime.split(':')[0]);
      const resEnd = parseInt(res.endTime.split(':')[0]);
      const slotTime = parseInt(time.split(':')[0]);
      return slotTime >= resStart && slotTime < resEnd;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'occupied': return 'bg-red-100 text-red-800';
      case 'maintenance': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return '사용 가능';
      case 'occupied': return '사용 중';
      case 'maintenance': return '점검 중';
      default: return status;
    }
  };

  const getRoomTypeText = (type: string) => {
    switch (type) {
      case 'vip': return 'VIP';
      case 'standard': return '일반';
      case 'fitting': return '피팅룸';
      default: return type;
    }
  };

  const getRoomTypeColor = (type: string) => {
    switch (type) {
      case 'vip': return 'bg-purple-100 text-purple-800';
      case 'standard': return 'bg-blue-100 text-blue-800';
      case 'fitting': return 'bg-teal-100 text-teal-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">드레스룸 관리</h1>
              <p className="text-sm text-gray-600 mt-1">드레스룸 사용 현황 및 예약 관리</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddRoomModal(true)}
                className="bg-teal-600 text-white px-5 py-2.5 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-add-line"></i>
                드레스룸 추가
              </button>
              <button
                onClick={() => setShowReservationModal(true)}
                className="bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-calendar-line"></i>
                예약 등록
              </button>
            </div>
          </div>

          {/* 탭 메뉴 */}
          <div className="flex gap-2 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('rooms')}
              className={`px-6 py-3 font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'rooms'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              드레스룸 현황
            </button>
            <button
              onClick={() => setActiveTab('reservations')}
              className={`px-6 py-3 font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'reservations'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              예약 타임테이블
            </button>
          </div>

          {/* 드레스룸 현황 */}
          {activeTab === 'rooms' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rooms.map(room => (
                <div key={room.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">{room.name}</h3>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoomTypeColor(room.type)}`}>
                          {getRoomTypeText(room.type)}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteRoom(room.id)}
                      className="text-red-600 hover:text-red-700 cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getStatusColor(room.status)}`}>
                      {getStatusText(room.status)}
                    </span>
                  </div>

                  {/* 오늘의 예약 현황 */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">오늘의 예약</p>
                    {reservations
                      .filter(res => res.roomId === room.id && res.date === new Date().toISOString().split('T')[0])
                      .map(res => (
                        <div key={res.id} className="text-sm text-gray-700 mb-2">
                          <div className="flex justify-between">
                            <span className="font-medium">{res.startTime} - {res.endTime}</span>
                            <span className="text-gray-500">{res.dressCount}벌</span>
                          </div>
                          <span className="text-gray-600">{res.customerName}</span>
                        </div>
                      ))}
                    {reservations.filter(res => res.roomId === room.id && res.date === new Date().toISOString().split('T')[0]).length === 0 && (
                      <p className="text-sm text-gray-400">예약 없음</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 예약 타임테이블 */}
          {activeTab === 'reservations' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">날짜 선택</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-700 sticky left-0 z-10">
                        시간
                      </th>
                      {rooms.map(room => (
                        <th key={room.id} className="border border-gray-200 bg-gray-50 px-4 py-3 text-center text-sm font-semibold text-gray-700 min-w-[180px]">
                          <div>{room.name}</div>
                          <div className="text-xs font-normal text-gray-500 mt-1">{getRoomTypeText(room.type)}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map(time => (
                      <tr key={time}>
                        <td className="border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                          {time}
                        </td>
                        {rooms.map(room => {
                          const reservation = getReservationForSlot(room.id, time);
                          return (
                            <td key={room.id} className="border border-gray-200 px-2 py-2">
                              {reservation && reservation.startTime === time ? (
                                <div className="bg-pink-50 border border-pink-200 rounded-lg p-3">
                                  <p className="text-sm font-semibold text-pink-900">{reservation.customerName}</p>
                                  <p className="text-xs text-pink-700 mt-1">{reservation.staff}</p>
                                  <p className="text-xs text-pink-600 mt-1">{reservation.startTime} - {reservation.endTime}</p>
                                  <p className="text-xs text-gray-600 mt-1">{reservation.purpose}</p>
                                  <p className="text-xs text-gray-500 mt-1">드레스 {reservation.dressCount}벌</p>
                                </div>
                              ) : reservation ? (
                                <div className="bg-pink-50 h-full"></div>
                              ) : (
                                <div className="h-full"></div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 드레스룸 추가 모달 */}
      {showAddRoomModal && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">드레스룸 추가</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">드레스룸 이름</label>
                <input
                  type="text"
                  value={newRoom.name}
                  onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                  placeholder="예: VIP 드레스룸 C"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">룸 타입</label>
                <select
                  value={newRoom.type}
                  onChange={(e) => setNewRoom({ ...newRoom, type: e.target.value as 'vip' | 'standard' | 'fitting' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="vip">VIP</option>
                  <option value="standard">일반</option>
                  <option value="fitting">피팅룸</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddRoomModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
              <button
                onClick={handleAddRoom}
                className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 예약 등록 모달 */}
      {showReservationModal && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">예약 등록</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">드레스룸 선택</label>
                <select
                  value={newReservation.roomId}
                  onChange={(e) => setNewReservation({ ...newReservation, roomId: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">선택하세요</option>
                  {rooms.map(room => (
                    <option key={room.id} value={room.id}>{room.name} ({getRoomTypeText(room.type)})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">고객명</label>
                <input
                  type="text"
                  value={newReservation.customerName}
                  onChange={(e) => setNewReservation({ ...newReservation, customerName: e.target.value })}
                  placeholder="고객 이름"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">담당 스타일리스트</label>
                <input
                  type="text"
                  value={newReservation.staff}
                  onChange={(e) => setNewReservation({ ...newReservation, staff: e.target.value })}
                  placeholder="스타일리스트 이름"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">날짜</label>
                <input
                  type="date"
                  value={newReservation.date}
                  onChange={(e) => setNewReservation({ ...newReservation, date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">시작 시간</label>
                  <input
                    type="time"
                    value={newReservation.startTime}
                    onChange={(e) => setNewReservation({ ...newReservation, startTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">종료 시간</label>
                  <input
                    type="time"
                    value={newReservation.endTime}
                    onChange={(e) => setNewReservation({ ...newReservation, endTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">피팅 목적</label>
                <input
                  type="text"
                  value={newReservation.purpose}
                  onChange={(e) => setNewReservation({ ...newReservation, purpose: e.target.value })}
                  placeholder="예: 웨딩드레스 피팅"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">드레스 수량</label>
                <input
                  type="number"
                  value={newReservation.dressCount}
                  onChange={(e) => setNewReservation({ ...newReservation, dressCount: parseInt(e.target.value) || 1 })}
                  min="1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowReservationModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
              <button
                onClick={handleAddReservation}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                등록
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
